import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Details from "Catalog/Edit/shared/components/Details"
import EditLayout from "Catalog/Edit/shared/components/EditLayout"
import useDelayedRenderedItem from "Catalog/Edit/shared/hooks/useDelayedRenderedItem"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { ChargeId } from "shared/hubrise/ids"
import { ICharge } from "shared/hubrise/models"
import * as Charge from "shared/hubrise/utils/Charge"

import Content from "./Content"
import Navigator from "./Navigator"
import Toolbar from "./Toolbar"

const Charges = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ chargeId: string }>()

  const { catalog } = useEditor()
  const items = catalog.data.charges

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.editPath("charges")

  const selectedItem = useFindItemOrRedirect<ICharge>(
    params.chargeId,
    (id) => Charge.byId(catalog, ChargeId(id)),
    indexPath,
  )

  const renderedItem = useDelayedRenderedItem(selectedItem)

  React.useEffect(() => {
    visit("charge", selectedItem)
  }, [selectedItem, visit])

  useKeyboardNavigation<ICharge>({
    item: selectedItem,
    collection: catalog.data.charges,
    indexPath: () => indexPath,
    showPath: (charge) => catalogNavigation.editPath("charge", charge.id),
  })

  return (
    <EditLayout
      toolbar={<Toolbar charge={renderedItem} />}
      navigator={items.length > 0 ? <Navigator items={items} selectedId={selectedItem?.id} /> : null}
      details={
        renderedItem ? (
          <Details.Main testId="catalog-details" resourceId={renderedItem.id}>
            <Content charge={renderedItem} />
          </Details.Main>
        ) : null
      }
      none={t("charge.none")}
      noneSelected={t("charge.none_selected")}
      testId="catalog-charges"
    />
  )
}

export default Charges
